import type { Group, Pouch } from '../../types';
import {
  generateRoundedRectPath,
  snapToGrid,
  translatePath,
  syncGroupMembershipFromPouchPositions,
} from '../../logic/GroupLogic';
import { reconcileGroupsToFitMembers } from '../../logic/PouchPlacementLogic';
import {
  CLUSTER_BUFFER_Y,
  PLACEMENT_GAP,
  PLACEMENT_ORIGIN_LEFT,
  PLACEMENT_ORIGIN_TOP,
  computeBrickLayout,
  computeClusterBounds,
  computeSplitGroupBounds,
  computeSplitGroupInsets,
  getGroupPathBounds,
  placeNewPouches,
  pouchPlacementDisplayLabel,
  replicaSubtotalBottomPadPx,
  type Rect,
} from '../../logic/PouchPlacementLogic';

function groupBoundsStrictlyInside(inner: Rect, outer: Rect): boolean {
  return (
    inner.left > outer.left &&
    inner.right < outer.right &&
    inner.top > outer.top &&
    inner.bottom < outer.bottom
  );
}

/** getGroupPathBounds フォールバック付きの物理包含（Jest でも親子判定が効く） */
function isGroupPhysicallyContainedLocal(
  containedGroupId: string,
  containerGroupId: string,
  groups: Group[],
): boolean {
  const containedGroup = groups.find(g => g.id === containedGroupId);
  const containerGroup = groups.find(g => g.id === containerGroupId);
  if (!containedGroup || !containerGroup) return false;
  const containedBounds = getGroupPathBounds(containedGroup);
  const containerBounds = getGroupPathBounds(containerGroup);
  if (!containedBounds || !containerBounds) return false;
  return groupBoundsStrictlyInside(containedBounds, containerBounds);
}

function groupsPhysicallyContainingLocal(
  groupId: string,
  groups: Group[],
): string[] {
  const innerGroup = groups.find(g => g.id === groupId);
  const innerBounds = innerGroup ? getGroupPathBounds(innerGroup) : null;
  if (!innerBounds) {
    return groups
      .filter(
        g =>
          g.id !== groupId &&
          isGroupPhysicallyContainedLocal(groupId, g.id, groups),
      )
      .map(g => g.id);
  }
  return groups
    .filter(g => {
      if (g.id === groupId) return false;
      const outerBounds = getGroupPathBounds(g);
      if (!outerBounds) return false;
      return groupBoundsStrictlyInside(innerBounds, outerBounds);
    })
    .map(g => g.id);
}

interface GroupLayoutResult {
  pouchPositions: Map<string, { x: number; y: number }>;
  groupPaths: Map<string, string>;
  bounds: Rect;
}

function mergeRects(a: Rect | null, b: Rect): Rect {
  if (!a) return b;
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom),
  };
}

function translateRect(r: Rect, dx: number, dy: number): Rect {
  return {
    left: r.left + dx,
    top: r.top + dy,
    right: r.right + dx,
    bottom: r.bottom + dy,
  };
}

function translateLayoutResult(
  result: GroupLayoutResult,
  dx: number,
  dy: number,
): GroupLayoutResult {
  const pouchPositions = new Map<string, { x: number; y: number }>();
  result.pouchPositions.forEach((pos, id) => {
    pouchPositions.set(id, {
      x: snapToGrid(pos.x + dx),
      y: snapToGrid(pos.y + dy),
    });
  });
  const groupPaths = new Map<string, string>();
  result.groupPaths.forEach((path, id) => {
    groupPaths.set(id, translatePath(path, dx, dy));
  });
  return {
    pouchPositions,
    groupPaths,
    bounds: translateRect(result.bounds, dx, dy),
  };
}

function sortIdsByCurrentPosition(ids: string[], pouches: Pouch[]): string[] {
  const byId = new Map(pouches.map(p => [p.id, p]));
  return [...ids].sort((a, b) => {
    const pa = byId.get(a);
    const pb = byId.get(b);
    if (!pa || !pb) return 0;
    if (pa.y !== pb.y) return pa.y - pb.y;
    return pa.x - pb.x;
  });
}

function sortGroupsByBounds(a: Group, b: Group): number {
  const ba = getGroupPathBounds(a);
  const bb = getGroupPathBounds(b);
  if (!ba || !bb) return 0;
  if (ba.top !== bb.top) return ba.top - bb.top;
  return ba.left - bb.left;
}

function getDirectChildGroupIds(parentId: string, groups: Group[]): string[] {
  const nested = groups.filter(
    g =>
      g.id !== parentId &&
      isGroupPhysicallyContainedLocal(g.id, parentId, groups),
  );
  return nested
    .filter(
      g =>
        !nested.some(
          other =>
            other.id !== g.id &&
            isGroupPhysicallyContainedLocal(g.id, other.id, groups),
        ),
    )
    .sort(sortGroupsByBounds)
    .map(g => g.id);
}

function getRootGroupIds(groups: Group[]): string[] {
  return groups
    .filter(g => groupsPhysicallyContainingLocal(g.id, groups).length === 0)
    .sort(sortGroupsByBounds)
    .map(g => g.id);
}

function brickLayoutOptionsForPouches(
  memberPouches: Pouch[],
): {
  pouchTitles: (string | undefined)[];
  memberMultipliers: number[];
} {
  return {
    pouchTitles: memberPouches.map(p => pouchPlacementDisplayLabel(p)),
    memberMultipliers: memberPouches.map(p => p.multiplier ?? 1),
  };
}

function clusterMemberWidthForGroup(
  memberPouches: Pouch[],
  wrapSingle = true,
): number {
  const layoutOptions = brickLayoutOptionsForPouches(memberPouches);
  const layoutAtOrigin = computeBrickLayout(
    memberPouches.length,
    0,
    0,
    'topleft',
    layoutOptions,
  );
  let width =
    computeClusterBounds(layoutAtOrigin, 0, 0).right -
    computeClusterBounds(layoutAtOrigin, 0, 0).left;
  if (wrapSingle && memberPouches.length > 0 && memberPouches.length < 2) {
    const refLayout = computeBrickLayout(2, 0, 0, 'topleft', layoutOptions);
    const refWidth =
      computeClusterBounds(refLayout, 0, 0).right -
      computeClusterBounds(refLayout, 0, 0).left;
    width = Math.max(width, refWidth);
  }
  return width;
}

function layoutLeafGroup(
  group: Group,
  pouches: Pouch[],
  anchorLeft: number,
  anchorTop: number,
  placedPouches: Map<string, { x: number; y: number }>,
): GroupLayoutResult {
  const memberIds = sortIdsByCurrentPosition(
    (group.pouches ?? []).filter(id => {
      const p = pouches.find(x => x.id === id);
      return p && !p.isGroupTotalPouch;
    }),
    pouches,
  );
  const memberPouches = memberIds
    .map(id => pouches.find(p => p.id === id))
    .filter((p): p is Pouch => !!p);

  if (memberPouches.length === 0) {
    const bounds: Rect = {
      left: anchorLeft,
      top: anchorTop,
      right: snapToGrid(anchorLeft + 100),
      bottom: snapToGrid(anchorTop + 100),
    };
    const path = generateRoundedRectPath(
      bounds.left,
      bounds.top,
      bounds.right,
      bounds.bottom,
    );
    return {
      pouchPositions: new Map(),
      groupPaths: new Map([[group.id, path]]),
      bounds,
    };
  }

  const toPlaceIds = memberIds.filter(id => !placedPouches.has(id));
  const toPlacePouches = toPlaceIds
    .map(id => pouches.find(p => p.id === id))
    .filter((p): p is Pouch => !!p);

  const layoutOptions = brickLayoutOptionsForPouches(toPlacePouches);
  const positionsAtOrigin =
    toPlacePouches.length > 0
      ? computeBrickLayout(
          toPlacePouches.length,
          0,
          0,
          'topleft',
          layoutOptions,
        )
      : [];
  const titleForInsets = group.title?.trim() || '';
  const insets = computeSplitGroupInsets(
    !!titleForInsets,
    titleForInsets,
    clusterMemberWidthForGroup(memberPouches),
    replicaSubtotalBottomPadPx(memberPouches.map(p => p.multiplier ?? 1)),
  );

  let positions: { x: number; y: number }[] = [];
  if (toPlacePouches.length > 0) {
    const boundsAtOrigin = computeSplitGroupBounds(positionsAtOrigin, insets);
    const dx = anchorLeft - boundsAtOrigin.left;
    const dy = anchorTop - boundsAtOrigin.top;
    positions = positionsAtOrigin.map(p => ({
      x: snapToGrid(p.x + dx),
      y: snapToGrid(p.y + dy),
    }));
  }

  const positionById = new Map<string, { x: number; y: number }>();
  toPlaceIds.forEach((id, index) => {
    const pos = positions[index];
    if (pos) positionById.set(id, pos);
  });
  for (const id of memberIds) {
    const fixed = placedPouches.get(id);
    if (fixed) positionById.set(id, fixed);
  }

  const allPositions = memberIds
    .map(id => positionById.get(id))
    .filter((p): p is { x: number; y: number } => !!p);
  const bounds = computeSplitGroupBounds(allPositions, insets);
  const path = generateRoundedRectPath(
    bounds.left,
    bounds.top,
    bounds.right,
    bounds.bottom,
  );
  const pouchPositions = new Map(positionById);
  return {
    pouchPositions,
    groupPaths: new Map([[group.id, path]]),
    bounds,
  };
}

function layoutGroupAt(
  groupId: string,
  pouches: Pouch[],
  groups: Group[],
  anchorLeft: number,
  anchorTop: number,
  placedPouches: Map<string, { x: number; y: number }>,
): GroupLayoutResult {
  const group = groups.find(g => g.id === groupId);
  if (!group) {
    return {
      pouchPositions: new Map(),
      groupPaths: new Map(),
      bounds: { left: anchorLeft, top: anchorTop, right: anchorLeft, bottom: anchorTop },
    };
  }

  const childIds = getDirectChildGroupIds(groupId, groups);

  if (childIds.length === 0) {
    const result = layoutLeafGroup(
      group,
      pouches,
      anchorLeft,
      anchorTop,
      placedPouches,
    );
    result.pouchPositions.forEach((pos, id) => {
      placedPouches.set(id, pos);
    });
    return result;
  }

  const pouchPositions = new Map<string, { x: number; y: number }>();
  const groupPaths = new Map<string, string>();
  const childMemberIds = new Set<string>();

  let cursorX = anchorLeft;
  let contentUnion: Rect | null = null;

  for (const childId of childIds) {
    const childResult = layoutGroupAt(
      childId,
      pouches,
      groups,
      cursorX,
      anchorTop,
      placedPouches,
    );
    childResult.pouchPositions.forEach((pos, id) => {
      pouchPositions.set(id, pos);
    });
    childResult.groupPaths.forEach((path, id) => {
      groupPaths.set(id, path);
    });
    contentUnion = mergeRects(contentUnion, childResult.bounds);
    const childPath = childResult.groupPaths.get(childId);
    if (childPath) {
      const pathBounds = getGroupPathBounds({
        id: childId,
        path: childPath,
        pouches: [],
      });
      if (pathBounds) {
        contentUnion = mergeRects(contentUnion, pathBounds);
      }
    }
    cursorX = childResult.bounds.right + PLACEMENT_GAP;

    const childGroup = groups.find(g => g.id === childId);
    for (const id of childGroup?.pouches ?? []) {
      childMemberIds.add(id);
    }
  }

  const orphanIds = sortIdsByCurrentPosition(
    (group.pouches ?? []).filter(id => {
      const p = pouches.find(x => x.id === id);
      return (
        p &&
        !p.isGroupTotalPouch &&
        !childMemberIds.has(id) &&
        !placedPouches.has(id)
      );
    }),
    pouches,
  );

  if (orphanIds.length > 0) {
    const orphanResult = layoutLeafGroup(
      { ...group, pouches: orphanIds },
      pouches,
      cursorX,
      anchorTop,
      placedPouches,
    );
    orphanResult.pouchPositions.forEach((pos, id) => {
      pouchPositions.set(id, pos);
      placedPouches.set(id, pos);
    });
    orphanResult.groupPaths.forEach((path, id) => {
      groupPaths.set(id, path);
    });
    contentUnion = mergeRects(contentUnion, orphanResult.bounds);
  }

  const memberPouches = (group.pouches ?? [])
    .map(id => pouches.find(p => p.id === id))
    .filter((p): p is Pouch => !!p && !p.isGroupTotalPouch);
  const titleForInsets = group.title?.trim() || '';
  const insets = computeSplitGroupInsets(
    !!titleForInsets,
    titleForInsets,
    clusterMemberWidthForGroup(memberPouches),
    replicaSubtotalBottomPadPx(memberPouches.map(p => p.multiplier ?? 1)),
  );

  const union = contentUnion ?? {
    left: anchorLeft,
    top: anchorTop,
    right: anchorLeft,
    bottom: anchorTop,
  };
  const bounds: Rect = {
    left: snapToGrid(union.left - insets.left),
    top: snapToGrid(union.top - insets.top),
    right: snapToGrid(union.right + insets.right),
    bottom: snapToGrid(union.bottom + insets.bottom),
  };
  const path = generateRoundedRectPath(
    bounds.left,
    bounds.top,
    bounds.right,
    bounds.bottom,
  );
  groupPaths.set(groupId, path);

  return { pouchPositions, groupPaths, bounds };
}

/**
 * シート配置を整頓する（親子構造・色・タイトル・値は維持、座標と group path のみ更新）。
 */
export function tidySheetLayout(
  pouches: Pouch[],
  groups: Group[],
): { pouches: Pouch[]; groups: Group[] } {
  const pouchesBeforeTidy = pouches.map(p => ({ ...p }));
  const groupsBeforeTidy = groups.map(g => ({ ...g }));
  const rootIds = getRootGroupIds(groups);
  const allPouchPositions = new Map<string, { x: number; y: number }>();
  const allGroupPaths = new Map<string, string>();
  const placedPouches = new Map<string, { x: number; y: number }>();

  let cursorY = PLACEMENT_ORIGIN_TOP;
  const cursorX = PLACEMENT_ORIGIN_LEFT;

  for (const rootId of rootIds) {
    const result = layoutGroupAt(
      rootId,
      pouches,
      groups,
      cursorX,
      cursorY,
      placedPouches,
    );
    const shifted = translateLayoutResult(
      result,
      cursorX - result.bounds.left,
      cursorY - result.bounds.top,
    );
    shifted.pouchPositions.forEach((pos, id) => {
      allPouchPositions.set(id, pos);
    });
    shifted.groupPaths.forEach((path, id) => {
      allGroupPaths.set(id, path);
    });
    cursorY = shifted.bounds.bottom + CLUSTER_BUFFER_Y;
  }

  const placedPouchIds = new Set(allPouchPositions.keys());
  const ungrouped = pouches.filter(
    p => !p.isGroupTotalPouch && !placedPouchIds.has(p.id),
  );

  let resultPouches = pouches.map(p => {
    const pos = allPouchPositions.get(p.id);
    return pos ? { ...p, x: pos.x, y: pos.y } : p;
  });
  let resultGroups = groups.map(g => {
    const path = allGroupPaths.get(g.id);
    return path ? { ...g, path } : g;
  });

  if (ungrouped.length > 0) {
    const plan = placeNewPouches({
      count: ungrouped.length,
      pouches: resultPouches.filter(p => placedPouchIds.has(p.id)),
      groups: resultGroups,
      mode: 'global',
      pouchTitles: ungrouped.map(p => pouchPlacementDisplayLabel(p)),
      memberMultipliers: ungrouped.map(p => p.multiplier ?? 1),
    });
    if (plan) {
      ungrouped.forEach((pouch, index) => {
        const pos = plan.positions[index];
        if (!pos) return;
        resultPouches = resultPouches.map(p =>
          p.id === pouch.id ? { ...p, x: pos.x, y: pos.y } : p,
        );
      });
    }
  }

  const fitted = reconcileGroupsToFitMembers(
    pouchesBeforeTidy,
    resultPouches,
    resultGroups,
    groupsBeforeTidy,
  );
  const syncedGroups = syncGroupMembershipFromPouchPositions(
    fitted.pouches,
    fitted.groups,
  );
  return {
    pouches: fitted.pouches,
    groups: syncedGroups,
  };
}
